import { BusinessObjectBase } from './base';
import type { Interview } from './interview';

export interface InterviewObservationData {
  readonly id: number;
  readonly enthusiasmScore?: number;
  readonly azerbaijaniLevel?: string;
  readonly englishLevel?: string;
  readonly russianLevel?: string;
  readonly persianLevel?: string;
  readonly frenchLevel?: string;
  readonly italianLevel?: string;
  readonly spanishLevel?: string;
  readonly germanLevel?: string;
  readonly arabicLevel?: string;
  readonly otherLanguage?: string;
  readonly shiftPreference?: string;
  readonly trainingAvailability?: string;
  readonly lateShift?: string;
  readonly specialRequest?: string;
  readonly needAdditionalCheck?: string;
  readonly totalScores?: number;
  readonly interviewId: number;
  readonly interview?: Interview;
}

export interface InterviewObservation extends Readonly<InterviewObservationData> {}
export class InterviewObservation extends BusinessObjectBase {
  static readonly entityName = 'InterviewObservation' as const;
  static readonly plural = 'interview-observations';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<InterviewObservation[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new InterviewObservation(data));
  }

  static async findOne(filter?: any): Promise<InterviewObservation | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new InterviewObservation(data) : null;
  }

  static async findById(
    id: number,
    filter?: any,
  ): Promise<InterviewObservation> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new InterviewObservation(data);
  }

  static async create(
    data: Omit<InterviewObservationData, 'id'>,
  ): Promise<InterviewObservation> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new InterviewObservation(result);
  }

  static async updateById(
    id: number,
    data: Partial<InterviewObservationData>,
  ): Promise<InterviewObservation> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new InterviewObservation(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
